import { useEffect, useState } from "react";

// Shared pieces for the free, no-login share pages (?page=archetype, ?page=milan).
// Light, saffron, big type — made for a first-time visitor on a phone.

export const API = (process.env.REACT_APP_API_URL || "http://localhost:8000").replace(/\/$/, "");
export const SITE = "https://vedsanga.com";
export const PLAY_URL = process.env.REACT_APP_PLAY_URL || "";

export const C = {
  bg: "#FFFBF3", card: "#FFFFFF", warm: "#FFF4E2", text: "#1F1A14", dim: "#4A4036",
  accent: "#E8832A", accentDark: "#B85E12", gold: "#D4AF37", border: "#EADFCB", wa: "#1E8E4E",
};

export const FONT = "Inter, 'Noto Sans Devanagari', 'Noto Sans Telugu', 'Noto Sans Tamil', 'Noto Sans Kannada', sans-serif";

export const LANGS = [["en", "English"], ["hi", "हिन्दी"], ["te", "తెలుగు"], ["ta", "தமிழ்"], ["kn", "ಕನ್ನಡ"], ["mr", "मराठी"]];

export const MONTHS = {
  en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
  hi: ["जन", "फ़र", "मार्च", "अप्रै", "मई", "जून", "जुला", "अग", "सित", "अक्टू", "नव", "दिस"],
  te: ["జన", "ఫిబ్ర", "మార్చి", "ఏప్రి", "మే", "జూన్", "జులై", "ఆగ", "సెప్టెం", "అక్టో", "నవం", "డిసెం"],
  ta: ["ஜன", "பிப்", "மார்", "ஏப்", "மே", "ஜூன்", "ஜூலை", "ஆக", "செப்", "அக்", "நவ", "டிச"],
  kn: ["ಜನ", "ಫೆಬ್ರ", "ಮಾರ್ಚ್", "ಏಪ್ರಿ", "ಮೇ", "ಜೂನ್", "ಜುಲೈ", "ಆಗ", "ಸೆಪ್ಟೆಂ", "ಅಕ್ಟೋ", "ನವೆಂ", "ಡಿಸೆಂ"],
  mr: ["जाने", "फेब्रु", "मार्च", "एप्रि", "मे", "जून", "जुलै", "ऑग", "सप्टें", "ऑक्टो", "नोव्हें", "डिसें"],
};

const DMY = {
  en: ["Day", "Month", "Year"], hi: ["दिन", "महीना", "वर्ष"], te: ["రోజు", "నెల", "సంవత్సరం"],
  ta: ["நாள்", "மாதம்", "ஆண்டு"], kn: ["ದಿನ", "ತಿಂಗಳು", "ವರ್ಷ"], mr: ["दिवस", "महिना", "वर्ष"],
};

export const fill = (s, o) => s.replace(/\{(\w+)\}/g, (_, k) => (o[k] ?? ""));
export const nameIn = (obj, lang) => (obj && (obj[lang] || obj.en)) || "";

/** Language: saved site language, else browser language, else English. Saved on change. */
export function usePublicLang(dict) {
  const [lang, setLang] = useState(() => {
    try { const l = localStorage.getItem("app_lang"); if (dict[l]) return l; } catch {}
    const nav = (navigator.language || "en").slice(0, 2);
    return dict[nav] ? nav : "en";
  });
  useEffect(() => { try { localStorage.setItem("app_lang", lang); } catch {} }, [lang]);
  return [lang, setLang];
}

export const sel = {
  minHeight: 52, fontSize: 17, padding: "0 10px", borderRadius: 10, border: `1.5px solid ${C.border}`,
  background: C.card, color: C.text, fontFamily: "Inter, sans-serif", flex: 1, minWidth: 0,
};

export const btn = (bg, fg = "#fff") => ({
  minHeight: 52, width: "100%", border: "none", borderRadius: 12, background: bg, color: fg,
  fontSize: 17, fontWeight: 700, fontFamily: "Inter, sans-serif", cursor: "pointer", padding: "0 16px",
});

export const label = { display: "block", fontSize: 16, fontWeight: 600, color: C.text, marginBottom: 8 };

/** Day / month / year selects — easier than a date input for older users. value = {d, m, y}. */
export function DobSelect({ lang, value, onChange }) {
  const [dl, ml, yl] = DMY[lang] || DMY.en;
  const thisYear = new Date().getFullYear();
  const years = Array.from({ length: thisYear - 1929 }, (_, i) => thisYear - i);
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  return (
    <div style={{ display: "flex", gap: 8 }}>
      <select aria-label={dl} value={value.d} onChange={set("d")} style={sel}>
        <option value="">{dl}</option>
        {Array.from({ length: 31 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
      </select>
      <select aria-label={ml} value={value.m} onChange={set("m")} style={{ ...sel, flex: 1.3 }}>
        <option value="">{ml}</option>
        {(MONTHS[lang] || MONTHS.en).map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
      </select>
      <select aria-label={yl} value={value.y} onChange={set("y")} style={{ ...sel, flex: 1.3 }}>
        <option value="">{yl}</option>
        {years.map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  );
}

export const dobReady = (v) => Boolean(v.d && v.m && v.y);
export const dobIso = (v) => `${v.y}-${String(v.m).padStart(2, "0")}-${String(v.d).padStart(2, "0")}`;

export function Header({ lang, setLang }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 20 }}>
      <a href={SITE} style={{ fontSize: 20, fontWeight: 800, color: C.accentDark, textDecoration: "none" }}>🔱 VedSanga</a>
      <select aria-label="Language" value={lang} onChange={(e) => setLang(e.target.value)} style={{ ...sel, flex: "0 0 auto", minHeight: 44, fontSize: 16 }}>
        {LANGS.map(([k, n]) => <option key={k} value={k}>{n}</option>)}
      </select>
    </div>
  );
}

/** Capture a DOM node and share it as a PNG (falls back to download). */
export async function shareNodeAsImage(node, fileName, text, bg = C.warm) {
  if (!node) return;
  try {
    const { default: html2canvas } = await import("html2canvas");
    const canvas = await html2canvas(node, { scale: 3, backgroundColor: bg, useCORS: true });
    const blob = await new Promise((ok) => canvas.toBlob(ok, "image/png"));
    const file = new File([blob], fileName, { type: "image/png" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], text });
    } else {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob); link.download = fileName; link.click();
      URL.revokeObjectURL(link.href);
    }
  } catch {}
}

export const openWhatsApp = (text) => window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
